import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import './styles/NoticeThemeEditorModal.scss';

import { Alert, Box, Stack, Typography } from '@mui/material';

import {
  Button,
  FormColorInput,
  FormTextInput,
  HStack,
  IModalPropsBase,
  Modal
} from '@dw/components/core';
import { useCreateNoticeThemeMutation, useUpdateNoticeThemeMutation } from '@dw/hooks/api/NoticeThemesApi';
import {
  INoticeThemeStyle,
  ISchoolNoticeTheme,
  NOTICE_THEME_MAX_BORDER_WIDTH,
  NOTICE_THEME_NAME_MAX_LENGTH
} from '@dw/models/notice';
import { componentClassName, getContrastRatio, getSpacingCss, isDarkBackgroundColor } from '@dw/utils';

import { NoticeThemePreview } from './NoticeThemePreview';

interface INoticeThemeForm {
  name: string;
  backgroundColor: string;
  borderColor: string;
  /** `FormTextInput type='number'` clears to null, so this is nullable and coalesced on the way out. */
  borderWidth: number | null;
  textColor: string;
}

/**
 * A new theme starts as a copy of Plain rather than as white on black.
 *
 * The first thing a studio sees is therefore already a notice, which is a better starting point to
 * adjust than a blank rectangle — and it means the preview is meaningful before anything is typed.
 */
const NEW_THEME: INoticeThemeForm = {
  name: '',
  backgroundColor: '#FFFCF8',
  borderColor: '#E8E0D6',
  borderWidth: 0,
  textColor: '#2A2521'
};

const initForm = (theme?: ISchoolNoticeTheme): INoticeThemeForm =>
  theme
    ? {
      name: theme.name,
      backgroundColor: theme.backgroundColor,
      borderColor: theme.borderColor,
      borderWidth: theme.borderWidth,
      textColor: theme.textColor
    }
    : { ...NEW_THEME };

/**
 * How a contrast ratio reads to somebody who does not know what a contrast ratio is.
 *
 * The number is shown as well as the words, because a studio who cares can compare two attempts,
 * and one who does not can read the sentence and move on. The thresholds are WCAG's: 4.5 for body
 * text, 3 for large text — below that a notice is genuinely hard to read on a phone in daylight.
 */
const contrastAdvice = (ratio: number | null) => {
  if (ratio === null) {
    return null;
  }

  const rounded = ratio.toFixed(1);

  if (ratio >= 4.5) {
    return { severity: 'ok' as const, message: `${rounded}:1 — easy to read` };
  }

  if (ratio >= 3) {
    return { severity: 'ok' as const, message: `${rounded}:1 — readable, but small text will strain` };
  }

  return {
    severity: 'warning' as const,
    message: `${rounded}:1 — hard to read. Try a darker text colour, or a paler background.`
  };
};

interface INoticeThemeEditorModalProps extends IModalPropsBase {
  /** Absent for a new theme. Present, and the modal edits that theme in place. */
  theme?: ISchoolNoticeTheme;
}

/**
 * Write a notice theme, or recolour one the school already has.
 *
 * One modal rather than two: create and edit differ in nothing a studio can see, and a second
 * near-identical dialog is how the two drift apart.
 *
 * Contrast is reported, never enforced. "Hard to read" is a judgement about a specific pair of
 * colours at a specific size on a screen the studio is looking at and we are not — so the portal
 * says what it measures and leaves the choice where it belongs.
 */
export const NoticeThemeEditorModal: FunctionComponent<INoticeThemeEditorModalProps> = (props: INoticeThemeEditorModalProps) => {

  const { open, onClose, theme } = props;

  const isEdit = !!theme;

  const [formData, setFormData] = useState<INoticeThemeForm>(initForm(theme));

  // Re-seeded on every open, and on the theme itself: the dialog stays mounted between openings, so
  // without this, editing one theme and then another would show the first one's colours.
  useEffect(() => {
    setFormData(initForm(theme));
  }, [open, theme]);

  const { mutate: createTheme, isPending: creating } = useCreateNoticeThemeMutation({ onSuccess: () => onClose() });
  const { mutate: updateTheme, isPending: updating } = useUpdateNoticeThemeMutation({ onSuccess: () => onClose() });

  const busy = creating || updating;

  const cn = componentClassName('notice-theme-editor-modal');

  /*
    Measured HERE rather than taken from the API, and this is the one place that is right: these
    colours have not been saved yet. Everywhere a stored theme is drawn, the flag comes off the wire.
  */
  const previewStyle: INoticeThemeStyle = useMemo(() => ({
    backgroundColor: formData.backgroundColor,
    borderColor: formData.borderColor,
    borderWidth: formData.borderWidth ?? 0,
    textColor: formData.textColor,
    isDarkBackground: isDarkBackgroundColor(formData.backgroundColor)
  }), [formData]);

  const advice = contrastAdvice(getContrastRatio(formData.textColor, formData.backgroundColor));

  const handleSubmit = (fd: INoticeThemeForm) => {
    const request = {
      name: fd.name.trim(),
      backgroundColor: fd.backgroundColor,
      borderColor: fd.borderColor,
      borderWidth: fd.borderWidth ?? 0,
      textColor: fd.textColor
    };

    if (isEdit) {
      updateTheme({ noticeThemeId: theme.noticeThemeId, ...request });
      return;
    }

    createTheme(request);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullScreenOnMobile
      title={isEdit ? 'Edit theme' : 'New theme'}
      contentTopPadding={getSpacingCss(2)}
      formWrapper={{
        formData,
        formDataChanged: setFormData,
        disabled: busy,
        onSubmit: handleSubmit
      }}
      actions={(
        <>
          <Button variant='text' color='error' onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant='text' type='submit' loading={busy}>
            {isEdit ? 'Save theme' : 'Add theme'}
          </Button>
        </>
      )}
    >
      <Stack rowGap={2}>

        {/*
          The preview leads, and sticks: a studio adjusting the third colour is still looking at what
          the first two did. It is the whole reason this dialog is not three text fields.
        */}
        <Stack className={cn('preview')} rowGap={1} sx={{ backgroundColor: 'background.paper' }}>
          <NoticeThemePreview style={previewStyle} />

          {!!advice && (advice.severity === 'warning' ? (
            <Alert severity='warning'>{advice.message}</Alert>
          ) : (
            <Typography variant='caption' textAlign='center'>{advice.message}</Typography>
          ))}
        </Stack>

        <FormTextInput<INoticeThemeForm>
          name='name'
          label='Theme name'
          required
          maxLength={NOTICE_THEME_NAME_MAX_LENGTH}
          margin='none'
          helperText='What your team will see in the colour picker'
        />

        {/* Stacks on a phone, side by side from tablet up — these are the pair a studio compares. */}
        <HStack columnGap={2} rowGap={2} alignItems='flex-start'>
          <Box flex={1} width='100%'>
            <FormColorInput<INoticeThemeForm> name='backgroundColor' label='Background' required sixDigitOnly />
          </Box>
          <Box flex={1} width='100%'>
            <FormColorInput<INoticeThemeForm> name='textColor' label='Text' required sixDigitOnly />
          </Box>
        </HStack>

        <HStack columnGap={2} rowGap={2} alignItems='flex-start'>
          <Box flex={1} width='100%'>
            <FormColorInput<INoticeThemeForm> name='borderColor' label='Border' required sixDigitOnly />
          </Box>
          <Box flex={1} width='100%'>
            <FormTextInput<INoticeThemeForm>
              name='borderWidth'
              label='Border thickness'
              type='number'
              min={0}
              max={NOTICE_THEME_MAX_BORDER_WIDTH}
              step={1}
              margin='none'
              helperText='0 for no border'
            />
          </Box>
        </HStack>
      </Stack>
    </Modal>
  );
};
